import AccountManager from './accountManager.js';
import d from './DOM.js';
import network from './network.js';
import Toast from './toast.js';

export default class AccountsPage {
	static init() {
		AccountsPage.loadAccounts();
		const copyBtn = d.get<HTMLButtonElement>('copy-customer-id');
		
		if (copyBtn) {
			copyBtn.addEventListener('click', () => {
				const idText = d.get<HTMLElement>('customer-id-display').innerText;
				
				navigator.clipboard.writeText(idText).then(() => {
					const originalText = copyBtn.textContent;
					copyBtn.textContent = 'Copied!';
					setTimeout(() => copyBtn.textContent = originalText, 2000);
				});
			});
		}
		
		const addBtn = d.get<HTMLButtonElement>('btn-add-account');
		if (addBtn) { addBtn.addEventListener('click', () => AccountsPage.openModal()); }
		
		const saveBtn = d.get<HTMLButtonElement>('btn-save-account');
		if (saveBtn) { saveBtn.addEventListener('click', AccountsPage.saveAccount); }
		
		const closeBtns = d.qsa<HTMLElement>('.close, .close-modal');
		closeBtns.forEach(btn => { btn.addEventListener('click', AccountsPage.closeModal); });
		
		// Close modal on outside click
		window.addEventListener('click', (e) => {
			const modal = d.get<HTMLElement>('account-modal');
			
			if (e.target === modal) {
				AccountsPage.closeModal();
			}
		});
	}
	
	static async loadAccounts() {
		const tbody = d.get<HTMLElement>('accounts-table-body');
		if (!tbody) { return; }
		
		tbody.innerHTML = '<tr><td colspan="6" style="text-align: center;">Loading...</td></tr>';
		
		try {
			const result = await network.ag('/api/accounts');
			
			if (result.ok) {
				const accounts = result.payload;
				tbody.innerHTML = '';
				
				if (accounts.length === 0) {
					tbody.innerHTML = '<tr><td colspan="6" style="text-align: center;">No accounts found.</td></tr>';
					return;
				}
				
				const fmtMoney = (n: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

				accounts.forEach((acc: any) => {
					const tr = document.createElement('tr');
					const metaApiStatus = acc.metaApiId ? '<span title="Charting Connected" style="color: #26a69a; cursor: help;">●</span>' : '<span title="Charting Not Connected" style="color: #64748b; cursor: help;">○</span>';
					
					tr.innerHTML = `
						<td>${acc.brokerName || 'Unknown'}</td>
						<td>${metaApiStatus} ${acc.accountNumber}</td>
						<td style="text-align: center;">
							<span class="badge ${acc.isLive ? 'badge-success' : 'badge-secondary'}">
								${acc.isLive ? 'LIVE' : 'DEMO'}
							</span>
						</td>
						<td style="text-align: right; font-family: monospace; font-weight: bold;">
							${fmtMoney(acc.balance || 0)}
						</td>
						<td style="text-align: center;">
							${acc.isDefault ? '<span style="color: var(--theme-accent-primary); font-size: 1.2rem;" title="Default Account">★</span>' : ''}
						</td>
						<td style="text-align: right;">
							<button class="btn-secondary btn-sm btn-balance" data-acc="${acc.accountNumber}" data-balance="${acc.balance}">Set Balance</button>
							<button class="btn-secondary btn-sm btn-edit" data-acc="${acc.accountNumber}">Edit</button>
							<button class="btn-danger btn-sm btn-delete" data-acc="${acc.accountNumber}">Delete</button>
						</td>
					`;
					
					tbody.appendChild(tr);
				});
				
				// Attach listeners
				d.qsa<HTMLButtonElement>('.btn-balance').forEach(btn => {
					btn.addEventListener('click', (e) => {
						const accNum = (e.target as HTMLElement).getAttribute('data-acc');
						const balance = (e.target as HTMLElement).getAttribute('data-balance');
						if (accNum) AccountsPage.updateBalance(accNum, parseFloat(balance || '0'));
					});
				});

				d.qsa<HTMLButtonElement>('.btn-edit').forEach(btn => {
					btn.addEventListener('click', (e) => {
						const accNum = (e.target as HTMLElement).getAttribute('data-acc');
						const acc = accounts.find((a: any) => a.accountNumber === accNum);
						if (acc) AccountsPage.openModal(acc);
					});
				});
				
				d.qsa<HTMLButtonElement>('.btn-delete').forEach(btn => {
					btn.addEventListener('click', (e) => {
						const accNum = (e.target as HTMLElement).getAttribute('data-acc');
						if (accNum) AccountsPage.deleteAccount(accNum);
					});
				});
			}
		} catch (e) {
			console.error(e);
			tbody.innerHTML = '<tr><td colspan="6" style="text-align: center; color: red;">Error loading accounts.</td></tr>';
		}
	}

	static async updateBalance(accountNumber: string, currentBalance: number) {
		const newBalanceStr = prompt(`Enter the actual current balance for account ${accountNumber}:`, currentBalance.toFixed(2));
		if (newBalanceStr === null) return;

		const balance = parseFloat(newBalanceStr);
		if (isNaN(balance)) {
			Toast.show('Invalid balance entered.', 'error');
			return;
		}

		try {
			const result = await network.ap('/api/accounts/balance', { accountNumber, balance });
			if (result.ok) {
				Toast.show('Balance updated and date synced.', 'success');
				AccountsPage.loadAccounts();
				AccountManager.init(); // Update global state
			} else {
				Toast.show('Failed to update balance.', 'error');
			}
		} catch (e) {
			console.error(e);
			Toast.show('An error occurred.', 'error');
		}
	}
	
	static openModal(account?: any) {
		const modal = d.get<HTMLElement>('account-modal');
		const title = d.get<HTMLElement>('modal-title');
		const brokerInput = d.get<HTMLInputElement>('account-broker-name');
		const numberInput = d.get<HTMLInputElement>('account-number');
		const serverInput = d.get<HTMLInputElement>('account-broker-server');
		const passwordInput = d.get<HTMLInputElement>('account-password');
		const liveCheck = d.get<HTMLInputElement>('account-is-live');
		const defaultCheck = d.get<HTMLInputElement>('account-is-default');
		const saveBtn = d.get<HTMLButtonElement>('btn-save-account');
		
		if (account) {
			title.innerText = 'Edit Account';
			brokerInput.value = account.brokerName || '';
			numberInput.value = account.accountNumber;
			numberInput.disabled = true; // Cannot change account number (primary key part)
			serverInput.value = account.brokerServer || '';
			passwordInput.value = account.accountPassword || '';
			liveCheck.checked = !!account.isLive;
			defaultCheck.checked = !!account.isDefault;
			saveBtn.setAttribute('data-original', account.accountNumber);
		} else {
			title.innerText = 'Add Account';
			brokerInput.value = '';
			numberInput.value = '';
			numberInput.disabled = false;
			serverInput.value = '';
			passwordInput.value = '';
			liveCheck.checked = false;
			defaultCheck.checked = false;
			saveBtn.removeAttribute('data-original');
		}
		
		modal.style.display = 'flex';
	}
	
	static closeModal() {
		const modal = d.get<HTMLElement>('account-modal');
		modal.style.display = 'none';
	}
	
	static async saveAccount() {
		const brokerName = d.get<HTMLInputElement>('account-broker-name').value.trim();
		const accountNumber = d.get<HTMLInputElement>('account-number').value.trim();
		const brokerServer = d.get<HTMLInputElement>('account-broker-server').value.trim();
		const accountPassword = d.get<HTMLInputElement>('account-password').value.trim();
		const isLive = d.get<HTMLInputElement>('account-is-live').checked;
		const isDefault = d.get<HTMLInputElement>('account-is-default').checked;
		const saveBtn = d.get<HTMLButtonElement>('btn-save-account');
		const originalAccountNumber = saveBtn.getAttribute('data-original');
		
		if (!brokerName || !accountNumber) {
			Toast.show('Please fill in all fields.', 'error');
			return;
		}
		
		try {
			const result = await network.ap('/api/accounts/save', {
				brokerName,
				accountNumber,
				brokerServer,
				accountPassword,
				isLive,
				isDefault,
				originalAccountNumber
			});
			
			if (result.ok) {
				const msg = result.payload?.metaApiId ? 'Account saved and MetaAPI connected!' : 'Account saved successfully.';
				Toast.show(msg, 'success');
				AccountsPage.closeModal();
				AccountsPage.loadAccounts();
				// Refresh global selector if needed
				AccountManager.init(); 
			} else {
				Toast.show(result.payload?.reason || 'Failed to save account.', 'error');
			}
		} catch (e) {
			console.error(e);
			Toast.show('An error occurred.', 'error');
		}
	}
	
	static async deleteAccount(accountNumber: string) {
		if (!confirm(`Are you sure you want to delete account ${accountNumber}? This will hide it from views but keep data for analysis.`)) {
			return;
		}
		
		try {
			const result = await network.ad(`/api/accounts/${accountNumber}`);
			
			if (result.ok) {
				Toast.show('Account deleted.', 'success');
				AccountsPage.loadAccounts();
				AccountManager.init();
			} else {
				Toast.show('Failed to delete account.', 'error');
			}
		} catch (e) {
			console.error(e);
			Toast.show('An error occurred.', 'error');
		}
	}
}
